import React, { useCallback, useId, useRef, useState } from 'react'

export interface DateTimeValue {
    month: number | null
    day: number | null
    year: number | null
    hours: number | null
    minutes: number | null
    seconds: number | null
    period: 'AM' | 'PM'
}

export interface DateFieldProps {
    variant?: 'primary' | 'secondary'
    label?: string
    description?: string
    error?: string
    value?: DateTimeValue
    defaultValue?: Partial<DateTimeValue>
    onChange?: (value: DateTimeValue) => void
    disabled?: boolean
    required?: boolean
    showTime?: boolean
    timezone?: string
    style?: React.CSSProperties
    /** Names the field for assistive tech when its visible label is rendered elsewhere (e.g. by DatePicker). */
    'aria-labelledby'?: string
    'aria-label'?: string
    /** Rendered inside the field at the right end, like a native date input's calendar icon. */
    suffix?: React.ReactNode
}

type SegmentId = 'month' | 'day' | 'year' | 'hours' | 'minutes' | 'seconds' | 'period'

interface SegConfig {
    id: SegmentId
    placeholder: string
    min: number
    max: number
    maxLen: number
}

const DATE_SEGS: SegConfig[] = [
    { id: 'month', placeholder: 'mm', min: 1, max: 12, maxLen: 2 },
    { id: 'day', placeholder: 'dd', min: 1, max: 31, maxLen: 2 },
    { id: 'year', placeholder: 'yyyy', min: 1900, max: 2099, maxLen: 4 },
]

const TIME_SEGS: SegConfig[] = [
    { id: 'hours', placeholder: '—', min: 1, max: 12, maxLen: 2 },
    { id: 'minutes', placeholder: '—', min: 0, max: 59, maxLen: 2 },
    { id: 'seconds', placeholder: '—', min: 0, max: 59, maxLen: 2 },
    { id: 'period', placeholder: 'AM', min: 0, max: 0, maxLen: 2 },
]

const EMPTY: DateTimeValue = {
    month: null,
    day: null,
    year: null,
    hours: null,
    minutes: null,
    seconds: null,
    period: 'AM',
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

export const DateField = ({
    variant = 'primary',
    label,
    description,
    error,
    value: controlledValue,
    defaultValue,
    onChange,
    disabled = false,
    required = false,
    showTime = true,
    timezone = 'EST',
    style,
    'aria-labelledby': ariaLabelledBy,
    'aria-label': ariaLabel,
    suffix,
}: DateFieldProps) => {
    const isSecondary = variant === 'secondary'
    const hasError = !!error
    const labelId = useId()

    // Mutable UI state — read/written synchronously in event handlers
    const uiRef = useRef({
        activeSegment: null as SegmentId | null,
        buffer: '',
        internalValue: { ...EMPTY, ...defaultValue } as DateTimeValue,
    })

    // Latest props/derived values — always current, even in memoized callbacks
    const ctx = useRef({
        controlledValue,
        onChange,
        showTime,
        disabled,
    })
    ctx.current = { controlledValue, onChange, showTime, disabled }

    // Trigger re-render for display updates
    const [tick, setTick] = useState(0)
    const rerender = useCallback(() => setTick((t) => t + 1), [])

    const containerRef = useRef<HTMLDivElement>(null)
    const suffixRef = useRef<HTMLSpanElement>(null)
    const segClickedRef = useRef(false)

    // Stable handler — reads everything from refs, no stale closures possible
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            // Keys from a suffix control are its own, not segment input.
            if (suffixRef.current?.contains(e.target as Node)) return
            const { disabled: isDisabled, showTime: withTime } = ctx.current
            const ui = uiRef.current
            const curSeg = ui.activeSegment
            if (!curSeg || isDisabled) return
            if (e.key === 'Tab') return
            e.preventDefault()

            const allSegs = withTime ? [...DATE_SEGS, ...TIME_SEGS] : DATE_SEGS
            const seg = allSegs.find((s) => s.id === curSeg)
            if (!seg) return

            const getVal = (): DateTimeValue => (ctx.current.controlledValue !== undefined ? ctx.current.controlledValue : ui.internalValue)

            const commit = (updates: Partial<DateTimeValue>) => {
                const next = { ...getVal(), ...updates }
                if (ctx.current.controlledValue === undefined) ui.internalValue = next
                ctx.current.onChange?.(next)
            }

            const advanceTo = (segId: SegmentId) => {
                ui.activeSegment = segId
                ui.buffer = ''
            }

            const advanceNext = (id: SegmentId) => {
                const idx = allSegs.findIndex((s) => s.id === id)
                if (idx < allSegs.length - 1) advanceTo(allSegs[idx + 1].id)
                else ui.activeSegment = null
            }

            const advancePrev = (id: SegmentId) => {
                const idx = allSegs.findIndex((s) => s.id === id)
                if (idx > 0) advanceTo(allSegs[idx - 1].id)
            }

            const val = getVal()

            // Period segment
            if (curSeg === 'period') {
                if (e.key === 'a' || e.key === 'A') {
                    commit({ period: 'AM' })
                    advanceNext('period')
                } else if (e.key === 'p' || e.key === 'P') {
                    commit({ period: 'PM' })
                    advanceNext('period')
                } else if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    commit({ period: val.period === 'AM' ? 'PM' : 'AM' })
                } else if (e.key === 'ArrowLeft') advancePrev('period')
                else if (e.key === 'ArrowRight') advanceNext('period')
                rerender()
                return
            }

            const flushBuffer = () => {
                if (ui.buffer) {
                    commit({ [curSeg]: clamp(parseInt(ui.buffer, 10), seg.min, seg.max) })
                    ui.buffer = ''
                }
            }

            if (e.key === 'ArrowLeft') {
                flushBuffer()
                advancePrev(curSeg)
                rerender()
                return
            }
            if (e.key === 'ArrowRight' || e.key === 'Enter') {
                flushBuffer()
                advanceNext(curSeg)
                rerender()
                return
            }

            if (e.key === 'ArrowUp') {
                const cur = (val as unknown as Record<string, number | null>)[curSeg]
                commit({ [curSeg]: cur === null ? seg.min : clamp(cur + 1, seg.min, seg.max) })
                ui.buffer = ''
                rerender()
                return
            }
            if (e.key === 'ArrowDown') {
                const cur = (val as unknown as Record<string, number | null>)[curSeg]
                commit({ [curSeg]: cur === null ? seg.max : clamp(cur - 1, seg.min, seg.max) })
                ui.buffer = ''
                rerender()
                return
            }
            if (e.key === 'Backspace' || e.key === 'Delete') {
                commit({ [curSeg]: null })
                ui.buffer = ''
                rerender()
                return
            }

            if (!/^\d$/.test(e.key)) return

            const newBuffer = ui.buffer + e.key
            const numVal = parseInt(newBuffer, 10)

            if (newBuffer.length >= seg.maxLen) {
                commit({ [curSeg]: clamp(numVal, seg.min, seg.max) })
                ui.buffer = ''
                advanceNext(curSeg)
            } else {
                ui.buffer = newBuffer
            }
            rerender()
        },
        [rerender] // truly stable — reads everything from refs
    )

    const handleBlur = (e: React.FocusEvent) => {
        if (containerRef.current?.contains(e.relatedTarget as Node)) return
        const ui = uiRef.current
        const { showTime: withTime } = ctx.current
        const curSeg = ui.activeSegment
        if (ui.buffer && curSeg && curSeg !== 'period') {
            const allSegs = withTime ? [...DATE_SEGS, ...TIME_SEGS] : DATE_SEGS
            const seg = allSegs.find((s) => s.id === curSeg)
            if (seg) {
                const next = { ...(ctx.current.controlledValue ?? ui.internalValue), [curSeg]: clamp(parseInt(ui.buffer, 10), seg.min, seg.max) }
                if (ctx.current.controlledValue === undefined) ui.internalValue = next
                ctx.current.onChange?.(next)
            }
        }
        ui.activeSegment = null
        ui.buffer = ''
        rerender()
    }

    // Read display values from ref at render time
    void tick // dependency for re-render
    const ui = uiRef.current
    const activeSegment = ui.activeSegment
    const buffer = ui.buffer
    const val = controlledValue !== undefined ? controlledValue : ui.internalValue
    const isFocused = activeSegment !== null

    const containerStateClasses = isFocused
        ? hasError
            ? 'ring-2 ring-danger'
            : 'ring-2 ring-accent'
        : hasError
          ? 'ring-1 ring-danger'
          : isSecondary
            ? ''
            : 'shadow-field'

    const renderSegment = (seg: SegConfig) => {
        const isActive = activeSegment === seg.id
        const rawVal = seg.id === 'period' ? val.period : (val[seg.id as keyof DateTimeValue] as number | null)
        const isFilled = rawVal !== null

        let display: string
        if (seg.id === 'period') {
            display = val.period
        } else if (isActive && buffer) {
            display = buffer
        } else if (isFilled) {
            display = seg.id === 'year' ? String(rawVal as number) : String(rawVal as number).padStart(2, '0')
        } else {
            display = seg.placeholder
        }

        const segStateClasses = isActive
            ? hasError
                ? 'bg-danger-soft text-danger-soft-foreground'
                : 'bg-accent-soft text-accent-soft-foreground'
            : isFilled
              ? 'text-foreground'
              : 'text-foreground-muted'

        return (
            <span
                key={seg.id}
                onMouseDown={(e) => {
                    e.preventDefault()
                    if (seg.id === 'period') {
                        const v = controlledValue !== undefined ? controlledValue : uiRef.current.internalValue
                        const next = { ...v, period: v.period === 'AM' ? ('PM' as const) : ('AM' as const) }
                        if (controlledValue === undefined) uiRef.current.internalValue = next
                        onChange?.(next)
                        uiRef.current.activeSegment = 'period'
                        uiRef.current.buffer = ''
                    } else {
                        segClickedRef.current = true
                        uiRef.current.activeSegment = seg.id
                        uiRef.current.buffer = ''
                        containerRef.current?.focus()
                    }
                    rerender()
                }}
                className={`inline-flex select-none items-center justify-center rounded-md px-0.5 text-sm font-normal leading-5 transition-colors duration-100 ${
                    seg.id === 'year' ? 'min-w-8' : 'min-w-[18px]'
                } ${segStateClasses} ${disabled ? 'cursor-not-allowed' : 'cursor-default'}`}
            >
                {display}
            </span>
        )
    }

    const sep = (char: string, key: string) => (
        <span key={key} className="select-none text-sm font-medium text-foreground-muted">
            {char}
        </span>
    )

    return (
        <div className={`flex flex-col gap-1 ${disabled ? 'opacity-50' : ''}`} style={style}>
            {label && (
                <label id={labelId} className={`text-sm font-medium ${hasError ? 'text-danger' : 'text-foreground'}`}>
                    {label}
                    {required && <span className="ml-0.5 text-danger">*</span>}
                </label>
            )}

            <div
                ref={containerRef}
                role="group"
                aria-labelledby={ariaLabelledBy ?? (label ? labelId : undefined)}
                aria-label={ariaLabel}
                aria-invalid={hasError || undefined}
                aria-disabled={disabled || undefined}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onFocus={(e) => {
                    if (suffixRef.current?.contains(e.target as Node)) return
                    if (segClickedRef.current) {
                        segClickedRef.current = false
                        return
                    }
                    if (!disabled) {
                        const segs = ctx.current.showTime ? [...DATE_SEGS, ...TIME_SEGS] : DATE_SEGS
                        uiRef.current.activeSegment = segs[0].id
                        uiRef.current.buffer = ''
                        rerender()
                    }
                }}
                data-field-box
                className={`box-border flex min-h-9 items-center gap-px overflow-hidden rounded-xl px-3 outline-none transition-shadow duration-150 ${
                    isSecondary ? 'bg-default' : 'bg-surface'
                } ${containerStateClasses} ${disabled ? 'cursor-not-allowed' : 'cursor-text'}`}
            >
                {renderSegment(DATE_SEGS[0])}
                {sep('/', 's1')}
                {renderSegment(DATE_SEGS[1])}
                {sep('/', 's2')}
                {renderSegment(DATE_SEGS[2])}
                {showTime && (
                    <>
                        {sep(',', 's3')}
                        {renderSegment(TIME_SEGS[0])}
                        {sep(':', 's4')}
                        {renderSegment(TIME_SEGS[1])}
                        {sep(':', 's5')}
                        {renderSegment(TIME_SEGS[2])}
                        {renderSegment(TIME_SEGS[3])}
                        <span className="ml-0.5 select-none text-sm font-normal leading-5 text-foreground-muted">{timezone}</span>
                    </>
                )}
                {suffix && (
                    <span ref={suffixRef} className="ml-auto flex shrink-0 items-center pl-2">
                        {suffix}
                    </span>
                )}
            </div>

            {!hasError && description && <span className="text-xs text-foreground-muted">{description}</span>}
            {hasError && <span className="text-xs text-danger">{error}</span>}
        </div>
    )
}
